import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

type DetailHeroVariant = "progression" | "perennial";

interface StatusConfig {
  label: string;
  color: "accent" | "success" | "warning" | "danger" | "neutral";
}

interface MetricItem {
  label: string;
  value: string;
}

interface TabItem {
  id: string;
  label: string;
  count?: number;
  content: React.ReactNode;
}

interface DetailHeroProps {
  variant: DetailHeroVariant;
  status?: StatusConfig;
  metrics?: MetricItem[];
  tabs: TabItem[];
  actions?: React.ReactNode;
  /** Controlled active-tab id. When omitted the first tab is used as defaultValue. */
  value?: string;
  /** Pair with `value` for controlled mode. */
  onValueChange?: (value: string) => void;
}

const statusColorMap: Record<StatusConfig["color"], string> = {
  accent: "border-[color:var(--color-accent)] text-[color:var(--color-accent)]",
  success:
    "border-[color:var(--color-success)] text-[color:var(--color-success)]",
  warning:
    "border-[color:var(--color-warning)] text-[color:var(--color-warning)]",
  danger: "border-[color:var(--color-danger)] text-[color:var(--color-danger)]",
  neutral:
    "border-[color:var(--color-border)] text-[color:var(--color-text-md)]",
};

function MetricsRow({ metrics }: { metrics: MetricItem[] }) {
  if (metrics.length === 0) return null;
  return (
    <div className="flex gap-6">
      {metrics.map((m) => (
        <div key={m.label} className="flex flex-col gap-0.5">
          <span className="typo-label text-[color:var(--color-text-md)]">
            {m.label}
          </span>
          <span className="text-[length:var(--font-size-body)] font-semibold tabular-nums text-[color:var(--color-text-hi)]">
            {m.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DetailHero({
  variant,
  status,
  metrics = [],
  tabs,
  actions,
  value,
  onValueChange,
}: DetailHeroProps) {
  const defaultTab = tabs[0]?.id;
  const tabsProps =
    value !== undefined
      ? { value, onValueChange }
      : { defaultValue: defaultTab };

  return (
    <div className="space-y-4">
      {/* Top row: status (progression) or metrics, plus actions */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {variant === "progression" && status && (
            <span
              data-testid="detail-hero-status"
              className={`inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label ${statusColorMap[status.color]}`}
            >
              {status.label}
            </span>
          )}
          {metrics.length > 0 && <MetricsRow metrics={metrics} />}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Tabs */}
      <Tabs {...tabsProps}>
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
            >
              {tab.label}
              {tab.count != null && (
                <Badge
                  variant="secondary"
                  className="ml-1.5 h-5 min-w-[20px] px-1.5 text-[11px]"
                >
                  {tab.count}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="mt-4">
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
